import { BarChartOutlined } from "@mui/icons-material";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format, parseISO, startOfHour } from "date-fns";
import { useEffect, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";

const HOURS = Array.from(
  { length: 24 },
  (_, i) => `${String(i).padStart(2, "0")}:00`,
);

const CustomTooltip = ({ active, payload, label, theme }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        px: 2,
        py: 1.5,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        minWidth: 120,
      }}
    >
      <Typography variant="caption" color="text.disabled" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} color="text.primary">
        {payload[0].value} {payload[0].value === 1 ? "ticket" : "tickets"}
      </Typography>
    </Box>
  );
};

const Chart = ({ refreshKey }) => {
  const theme = useTheme();
  const date = useRef(new Date().toISOString());
  const { tickets } = useTickets({ date: date.current, refreshKey });
  const currentHour = format(startOfHour(new Date()), "HH:mm");

  const [chartData, setChartData] = useState(
    HOURS.map((time) => ({ time, amount: 0 })),
  );

  useEffect(() => {
    setChartData(
      HOURS.map((time) => ({
        time,
        amount: tickets.filter(
          (t) => format(startOfHour(parseISO(t.createdAt)), "HH:mm") === time,
        ).length,
      })),
    );
  }, [tickets]);

  const totalToday = tickets.length;

  const coloredData = chartData.map((entry) => ({
    ...entry,
    fill:
      entry.time === currentHour
        ? theme.palette.warning.main
        : "url(#barGradient)",
  }));

  return (
    <Box>
      {/* Card header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <BarChartOutlined sx={{ fontSize: 19 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              {i18n.t("dashboard.charts.perDay.title").replace(": ", "")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ticket volume by hour
            </Typography>
          </Box>
        </Stack>

        <Chip
          label={`${totalToday} total`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600, borderRadius: 1.5 }}
        />
      </Stack>

      <Divider />

      {/* Chart */}
      <Box sx={{ px: 1, pt: 3, pb: 2, height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={coloredData}
            margin={{ top: 4, right: 24, bottom: 0, left: 0 }}
            barSize={14}
          >
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={theme.palette.primary.main}
                  stopOpacity={0.95}
                />
                <stop
                  offset="100%"
                  stopColor={theme.palette.primary.light}
                  stopOpacity={0.55}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="4 4"
              vertical={false}
              stroke={theme.palette.divider}
            />

            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              interval={2}
              tick={{
                fill: theme.palette.text.disabled,
                fontSize: 11,
              }}
              dy={8}
            />

            <YAxis
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              tick={{
                fill: theme.palette.text.disabled,
                fontSize: 12,
              }}
              width={32}
            />

            <Tooltip
              content={<CustomTooltip theme={theme} />}
              cursor={{ fill: theme.palette.action.hover, radius: 4 }}
            />

            <Bar
              dataKey="amount"
              radius={[5, 5, 0, 0]}
              fill="url(#barGradient)"
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Footer hint */}
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="text.disabled">
          Current hour highlighted in amber · Showing 00:00 – 23:00
        </Typography>
      </Box>
    </Box>
  );
};

export default Chart;
